// @ts-strict-ignore
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, Subject, timer } from 'rxjs';

import { LMPrintDialogInfo } from './lm-print-dialog.service';
import { LMUrlUtilService, NameValueMap, URLOptions } from '@services/utils/lm-url-util.service';
import { LMContextService } from '@services/lm-context.service';
import { AppConstants } from '@utils/app.constants';
import {
  catchError,
  concatMap,
  takeUntil,
} from 'rxjs/operators';
import { DocumentResource } from '@app/resources';

@Injectable()
export class LMPrintRequestService {
  constructor(
    private httpClient: HttpClient,
    private lmContextService: LMContextService,
    private lmUrlUtilService: LMUrlUtilService
  ) {}

  public getCommonRequestParams(printInfo: LMPrintDialogInfo): LMCommonPrintParams {
    let params: LMCommonPrintParams = {};

    // Optional parameters to be added when defined:
    if (printInfo.userFolder) {
      params.userFolder = printInfo.userFolder;
    }
    if (printInfo.cubeOverrides) {
      params.cubeOverrides = printInfo.cubeOverrides;
    } else if (printInfo.cubeOptions) {
      // Cube options contain array of objects for each dimension type such as context, filter and current cube
      // They are kept separate in different collections and DataViewCubeInfo object is built before fetching data.
      params.cubeOptions = printInfo.cubeOptions;
    }
    if (printInfo.fromResource) {
      // url util service encodes parameters
      params[printInfo.fromResource.resourceType] = printInfo.fromResource.generateResourceId(false);
    }
    if (printInfo.fromList) {
      // url util service encodes parameters
      params['fromlist'] = printInfo.fromList.generateResourceId(false);
    }
    // as of now, there is no possible way to identify a Matrix Spreadsheet and a Matrix Compare
    // they both have the same LPL syntax construct and the same spec
    // the only way to differentiate the two is via url item params
    // when item is present in the url, the form is Matrix Compare (hence ITEM compare)
    // absence of item in the url params means it is a Matrix Spreadsheet.
    // this is both true with export to csv and pdf
    if (printInfo?.dataView?.columnList && this.lmUrlUtilService.hasParam(window.location.href, 'item')) {
      const items: string[] = [];
      printInfo.dataView.columnList.forEach((dataView: LMDataView) => {
        items.push(dataView.resourceId);
      });
      params['item'] = items;
    }
    // Append PagingInfo from DataViewSet
    const pagingInfo = (printInfo.dataViewSet) ? printInfo.dataViewSet.pagingInfo : {};
    if (pagingInfo.filter) {
      params.filter = pagingInfo.filter;
    }
    if (pagingInfo.sortOrderName) {
      params.sortordername = pagingInfo.sortOrderName;
    }
    if (pagingInfo.isAscending !== undefined) {
      params.isascending = pagingInfo.isAscending;
    }

    // TODO: Refactor this with backend and use filters from LMPrintRequestData
    // instead of having two separate places to send filter criteria
    if (printInfo.filterFields?.length) {
      const filterFields = this.lmUrlUtilService.filterFieldCriteriaToNameValueMap(printInfo.filterFields, 'fc');
      filterFields.forEach((filter: NameValueMap) => {
        params = { ...params, ...filter };
      });
    }
    return params;
  }

  public getConfigFeatureUseAllActorGroups(): boolean {
    // better for testing!!!
    return this.lmContextService.isConfigFeatureEnabled(AppConstants.FEATURE_UseAllActorGroups);
  }

  public getReportDistributionSelectUrl(): string {
    const url = this.lmUrlUtilService.getBaseUrl() + '/list/ReportDistributionGroup.ReportDistributionGroupDefaultList?' +
      'output=spec&type=select&select=ReportDistributionGroup.ReportDistributionGroup';
    return this.lmUrlUtilService.propagateSessionKeys(url, '&');
  }

  private buildUrl(options: LMRunPrintOptions): string {
    let cubeOptions = '';
    if (options?.params?.cubeOverrides) {
      cubeOptions = '&' + this.lmUrlUtilService.cubeOverridesToQueryString(options.params.cubeOverrides);
      delete options.params.cubeOverrides;
    }

    if (options?.params?.cubeOptions) {
      cubeOptions = '&' + this.lmUrlUtilService.objectToCubeQueryString(options.params.cubeOptions);
      delete options.params.cubeOptions;
    }

    const items: string[] = [];
    let itemStringified = '';

    if (options?.params?.item) {
      options.params.item.forEach( item => {
        items.push(this.lmUrlUtilService.objectToQueryString({ item }));
      });
      itemStringified = '&' + items.join('&');
      delete options.params.item;
    }

    const urlOptions = {
      resource: options.resource,
      params: options.params
    } as URLOptions;
    return this.lmUrlUtilService.buildUrl(urlOptions) + cubeOptions + itemStringified;
  }

  public runPrint(options: LMRunPrintOptions): Observable<LMPrintResponse> {
    const url = this.buildUrl(options);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json;charset=utf-8' });

    return this.httpClient.post<LMPrintResponse>(url, JSON.stringify(options.requestData), { headers });
  }

  public runPrintForeground(options: LMRunPrintOptions): Observable<Blob> {
    const url = this.buildUrl(options);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json;charset=utf-8' });

    return this.httpClient.post(url, JSON.stringify(options.requestData), { headers, responseType: 'blob'});
  }

  public startPoll(asyncId: string, cancelPolling: Subject<boolean>, timeInterval = 1000): Observable<LMPrintPollResponse> {
    const documentResource = new DocumentResource({ resourceId: 'ScheduledDocumentStatusTask.GetStatus' }, '');
    const url = this.lmUrlUtilService.buildUrl({ resource: documentResource, params: { asyncId } });
    let i = 0;
    // Timer will complete after emitting one value.
    const intervalBackoff = iteration => timer(iteration * 1000);
    return timer(0, timeInterval)
      .pipe(
        // Get async status after 'iteration' seconds
        concatMap(() => intervalBackoff(++i)),
        concatMap(() => this.httpClient.get(url)),
        // If async status is 'ready', cancel polling
        // This subject will emit an event from subscribe block.
        takeUntil(cancelPolling),
        catchError((error) => {
          // Cancel polling for now, retry logic will be an improvement.
          cancelPolling.next(true);
          if (!error.status) {
            throw error;
          }
          return of(error);
        })
      );
  }
}
